import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Chip,
  useTheme,
  Stack,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
  Github,
  Google,
  User
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
export default function SignupPage() {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'acceptTerms' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle signup logic here
    console.log('Signup attempt with:', formData);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #fef2f2, #fdf2f8)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip
            icon={<Sparkles className="w-4 h-4" />}
            label="Join Bean"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontSize: '1rem',
              py: 2,
              px: 2,
              mb: 2
            }}
          />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Create Your Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Already have an account?{' '}
            <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              Sign in
            </Link>
          </Typography>
        </Box>

        {/* Signup Form */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 4
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Name Fields */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Email Field */}
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Fields */}
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Terms Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    required
                    sx={{
                      color: theme.palette.primary.main,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/privacy"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />

              {/* Sign Up Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!formData.acceptTerms}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  },
                  py: 1.5
                }}
                endIcon={<ArrowRight className="w-4 h-4" />}
              >
                Create Account
              </Button>
            </Stack>
          </form>

          <Divider sx={{ my: 4 }}>
            <Typography variant="body2" color="text.secondary">
              or sign up with
            </Typography>
          </Divider>

          {/* Social Signup Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FcGoogle className="w-5 h-5" />}
              sx={{
                py: 1.5,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              Google
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Github className="w-5 h-5" />}
              sx={{
                py: 1.5,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              GitHub
            </Button>
          </Stack>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            By creating an account, you agree to our{' '}
            <Link
              to="/terms"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link
              to="/privacy"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
